import { ArrowLeft, ChevronDown, ChevronLeft, ChevronRight, Phone } from "lucide-react";
import { Link } from "@/lib/nav";
import { Fragment, ReactNode } from "react";

import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import { cn } from "@/lib/utils";
import PrintButton from "@/modules/print/print-button";
import { INCL_SUPPLY_LABEL, INCL_VAT_LABEL, snapshotVatOf, SUPPLY_LABEL, TOTAL_LABEL, unitPriceHeading, VAT_LABEL, vatBasisLabel } from "@buildpy/core/modules/quote/snapshot-vat";
import { SnapshotData, SnapshotTrade } from "@buildpy/core/modules/quote/types";
import Money from "@/components/money";
import { formatDate, formatRate } from "@/modules/quote/ui/format";
import QuoteItemCard from "@/modules/quote/ui/item-card";
import { TRADE_GROUP_LABEL, TRADE_GROUPS } from "@buildpy/core/modules/template/trades";

// 폰(md 미만) 고객 견적서 — A4 문서 대신 요약(1-a)과 공종 상세(1-b)를 `?t=n` 으로 나눈다.

/** 공종 목록을 접는 기준 — 이보다 많으면 앞의 FOLD_SHOWN 개만 보이고 나머지는 「n개 더 보기」. */
export const FOLD_AT = 8;
export const FOLD_SHOWN = 6;

/** 인쇄 문서(quote-summary·quote-detail)와 같은 순서 — sort_order 오름차순. */
export const sortedTrades = (data: SnapshotData): SnapshotTrade[] =>
  [...data.trades].sort((a, b) => a.sort_order - b.sort_order);

/** `?t=` 값 → 공종 번호(1부터). 범위 밖·숫자 아님이면 null(요약을 보인다). */
export const tradeIndexOf = (raw: string | string[] | undefined, count: number): number | null => {
  const value = Array.isArray(raw) ? raw[0] : raw;
  if (!value || !/^\d+$/.test(value)) return null;
  const n = Number(value);
  return n >= 1 && n <= count ? n : null;
};

// 링크 앞부분 — 기본은 고객 링크 `/s/<token>`, 데모처럼 다른 경로에서 그릴 때만 basePath 로 바꾼다.
const baseOf = (token: string, basePath?: string): string => basePath ?? `/s/${token}`;
const tradeHrefIn = (base: string, n: number): string => `${base}?t=${n}`;

// 상태바 아래(top = 안전 영역)에 붙는다 — 그 위 빈자리는 감싸는 화면이 비운다(고객 링크 pt-safe · 데모 띠).
const Header = ({ children }: { children: ReactNode }) => (
  <header className="sticky top-(--safe-top) z-10 flex h-14 shrink-0 items-center justify-between gap-1 border-b border-border bg-card px-1">
    {children}
  </header>
);

const BottomBar = ({ children, className }: { children: ReactNode; className?: string }) => (
  <div className={cn("sticky bottom-0 z-10 mt-auto flex shrink-0 items-center gap-2 border-t border-border bg-card px-4 pb-safe-3.5 pt-2.5", className)} data-testid="quote-mobile-bar">
    {children}
  </div>
);

const Row = ({ label, value, strong, small }: { label: ReactNode; value: ReactNode; strong?: boolean; small?: boolean }) => (
  <div className={strong ? "mt-1.5 flex justify-between gap-3 border-t border-border pt-2.5 font-semibold" : cn("flex justify-between gap-3", small ? "py-0.5 text-sm" : "py-1.5")}>
    <dt className={strong ? "text-foreground" : "text-muted-foreground"}>{label}</dt>
    <dd className={cn("whitespace-nowrap tabular-nums", small && "text-muted-foreground")}>{value}</dd>
  </div>
);

const TradeLink = ({ base, trade, n }: { base: string; trade: SnapshotTrade; n: number }) => (
  <Link className="flex min-h-[52px] items-center gap-2 py-2 pl-4 pr-2 hover:bg-muted" href={tradeHrefIn(base, n)}>
    <span className="min-w-0 flex-1 text-foreground">{trade.name}</span>
    <Money className="text-foreground" value={trade.amount} />
    <ChevronRight aria-hidden className="size-4 shrink-0 text-muted-foreground" />
  </Link>
);

interface SummaryProps {
  data: SnapshotData;
  token: string;
  expiresAt: string;
  basePath?: string;
}

/** 1-a 견적 요약 — 금액 하나를 크게, 공종은 한 줄씩 눌러 들어간다. */
export const QuoteMobileSummary = ({ data, token, expiresAt, basePath }: SummaryProps) => {
  const { quote, customer, org } = data;
  const vat = snapshotVatOf(data);
  const base = baseOf(token, basePath);
  const trades = sortedTrades(data);
  const numberOf = new Map(trades.map((t, i) => [t, i + 1]));
  const subtotalOf = (group: (typeof TRADE_GROUPS)[number]) =>
    group === "BASIC" ? quote.subtotal_basic : quote.subtotal_style;

  return (
    <div className="flex min-h-screen flex-col" data-testid="quote-mobile-summary">
      <Header>
        <div className="min-w-0 pl-3">
          <p className="truncate text-lg font-semibold">{org.name}</p>
          <p className="truncate text-xs text-muted-foreground">
            견적서 · {quote.quote_no} v{quote.version}
          </p>
        </div>
        {org.tel && (
          <Button asChild className="size-11" size="icon" variant="ghost">
            <a aria-label={`전화 걸기 ${org.tel}`} href={`tel:${org.tel}`}>
              <Phone />
            </a>
          </Button>
        )}
      </Header>

      <div className="flex flex-col gap-3 px-4 pb-6 pt-3">
        <SectionCard className="flex flex-col gap-1.5 px-4 py-3.5" padding="none">
          <span className="text-xs text-muted-foreground">{customer.name} 고객님께 드리는 견적</span>
          <h1 className="text-2xl font-semibold leading-snug">{quote.title}</h1>
          {customer.site_address && <span className="text-sm text-muted-foreground">{customer.site_address}</span>}
          <div className="mt-2.5 border-t border-border pt-3">
            <p className="text-xs text-muted-foreground">{TOTAL_LABEL}</p>
            <p className="whitespace-nowrap text-4xl font-bold tracking-tight tabular-nums" data-testid="quote-mobile-total">
              <Money value={quote.total} />
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              견적일 {formatDate(quote.sent_at)} · 링크 {formatDate(expiresAt)}까지
            </p>
          </div>
        </SectionCard>

        <SectionCard className="px-4 py-3.5" padding="none">
          <div className="mb-1 flex items-baseline justify-between gap-3">
            <h2 className="text-md font-semibold">금액 구성</h2>
            <span className="text-xs text-muted-foreground" data-testid="quote-mobile-vat-basis">
              공종 금액 {vatBasisLabel(vat.mode)}
            </span>
          </div>
          <dl className="text-md" data-testid="quote-mobile-amounts">
            <Row label={TRADE_GROUP_LABEL.BASIC} value={<Money value={quote.subtotal_basic} />} />
            <Row label={TRADE_GROUP_LABEL.STYLE} value={<Money value={quote.subtotal_style} />} />
            <Row label={`관리비 (${formatRate(quote.mgmt_fee_rate)})`} value={<Money value={quote.mgmt_fee} />} />
            {quote.rounding_adjustment > 0 && <Row label="단수 정리" value={<span className="whitespace-nowrap">−<Money value={quote.rounding_adjustment} /></span>} />}
            {vat.mode === "EXCL" ? (
              <>
                <Row label={SUPPLY_LABEL} value={<Money value={vat.supply} />} />
                <Row label={VAT_LABEL} value={<Money value={vat.vat} />} />
                <Row label={TOTAL_LABEL} strong value={<Money value={vat.total} />} />
              </>
            ) : (
              <>
                <Row label={TOTAL_LABEL} strong value={<Money value={vat.total} />} />
                <Row label={INCL_SUPPLY_LABEL} small value={<Money value={vat.supply} />} />
                <Row label={INCL_VAT_LABEL} small value={<Money value={vat.vat} />} />
              </>
            )}
          </dl>
        </SectionCard>

        <SectionCard className="overflow-hidden" padding="none">
          <div className="flex items-center justify-between gap-3 px-4 pb-2.5 pt-3.5">
            <h2 className="text-md font-semibold">공종별 금액</h2>
            <span className="text-xs text-muted-foreground">눌러서 항목 보기</span>
          </div>
          {trades.length === 0 && <p className="px-4 pb-4 text-sm text-muted-foreground">공종이 없습니다.</p>}
          {TRADE_GROUPS.map((group) => {
            const list = trades.filter((t) => t.group === group);
            if (list.length === 0) return null;
            const folded = list.length > FOLD_AT;
            const shown = folded ? list.slice(0, FOLD_SHOWN) : list;
            const rest = folded ? list.slice(FOLD_SHOWN) : [];
            return (
              <Fragment key={group}>
                <div className="flex justify-between bg-muted px-4 py-2.5 text-sm font-semibold text-muted-foreground">
                  <span>
                    {TRADE_GROUP_LABEL[group]} · {list.length}
                  </span>
                  <Money value={subtotalOf(group)} />
                </div>
                <div className="divide-y divide-border" data-testid={`quote-mobile-group-${group}`}>
                  {shown.map((t) => (
                    <TradeLink key={`${t.trade}-${t.sort_order}`} base={base} n={numberOf.get(t)!} trade={t} />
                  ))}
                  {folded && (
                    <details className="group">
                      <summary className="flex min-h-[52px] cursor-pointer list-none items-center justify-center gap-1 text-primary group-open:hidden [&::-webkit-details-marker]:hidden">
                        {rest.length}개 더 보기
                        <ChevronDown aria-hidden className="size-4" />
                      </summary>
                      <div className="divide-y divide-border">
                        {rest.map((t) => (
                          <TradeLink key={`${t.trade}-${t.sort_order}`} base={base} n={numberOf.get(t)!} trade={t} />
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              </Fragment>
            );
          })}
        </SectionCard>

        {data.schedule && (
          <SectionCard as="details" className="group" padding="none">
            <summary className="flex min-h-[52px] cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 [&::-webkit-details-marker]:hidden">
              <span className="min-w-0">
                <span className="block text-md font-semibold">시공 일정</span>
                <span className="block text-xs text-muted-foreground tabular-nums">
                  {formatDate(data.schedule.start_date)} ~ {formatDate(data.schedule.end_date)} · 작업 {data.schedule.total_workdays}일
                </span>
              </span>
              <ChevronDown aria-hidden className="size-4 shrink-0 group-open:rotate-180" />
            </summary>
            <dl className="px-4 pb-3.5 text-sm">
              {data.schedule.phases.map((phase, i) => (
                <Row key={`${phase.name}-${i}`} label={phase.name} value={`${formatDate(phase.start_date)} ~ ${formatDate(phase.end_date)}`} />
              ))}
            </dl>
          </SectionCard>
        )}

        {quote.conditions.length > 0 && (
          <SectionCard as="details" className="group" padding="none">
            <summary className="flex min-h-[52px] cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 [&::-webkit-details-marker]:hidden">
              <span className="text-md font-semibold">견적 조건 · {quote.conditions.length}</span>
              <ChevronDown aria-hidden className="size-4 shrink-0 group-open:rotate-180" />
            </summary>
            <ol className="list-decimal space-y-1 pb-3.5 pl-8 pr-4 text-sm">
              {quote.conditions.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ol>
          </SectionCard>
        )}

        {quote.extra_notice && (
          <SectionCard className="border-warning-bg bg-warning-bg px-4 py-3.5" padding="none">
            <h2 className="mb-1.5 text-md font-semibold text-warning">견적에 포함되지 않는 비용</h2>
            <p className="whitespace-pre-wrap text-sm text-foreground">{quote.extra_notice}</p>
          </SectionCard>
        )}

        <p className="mt-1 text-center text-xs text-muted-foreground">
          이 링크는 {formatDate(expiresAt)}까지 열립니다 · 문의 {org.name}
        </p>
      </div>

      <BottomBar>
        {org.tel && (
          <Button asChild className="min-h-11 flex-1" variant="outline">
            <a href={`tel:${org.tel}`}>
              <Phone />
              전화 문의
            </a>
          </Button>
        )}
        <PrintButton className="min-h-11 flex-1" label="PDF 저장" size="default" />
      </BottomBar>
    </div>
  );
};

interface TradeProps {
  data: SnapshotData;
  token: string;
  /** 1부터 — `sortedTrades` 순서의 번호. */
  n: number;
  basePath?: string;
}

/** 1-b 공종 상세 — 항목 카드 + 이전/다음 공종. 이전/다음은 replace 라 뒤로가기 한 번이면 요약으로 돌아간다. */
export const QuoteMobileTrade = ({ data, token, n, basePath }: TradeProps) => {
  const base = baseOf(token, basePath);
  const trades = sortedTrades(data);
  const trade = trades[n - 1];
  const prev = n > 1 ? trades[n - 2] : null;
  const next = n < trades.length ? trades[n] : null;

  return (
    <div className="flex min-h-screen flex-col" data-testid="quote-mobile-trade">
      <Header>
        <Button asChild className="size-11" size="icon" variant="ghost">
          <Link aria-label="공종 목록으로" href={base}>
            <ArrowLeft />
          </Link>
        </Button>
        <h1 className="min-w-0 flex-1 truncate text-xl font-semibold">{trade.name}</h1>
        <Money className="pr-3 font-semibold" value={trade.amount} />
      </Header>

      <div className="flex flex-col gap-3 px-4 pb-6 pt-3">
        <div className="flex items-center justify-between gap-3 px-0.5 text-sm text-muted-foreground">
          <span className="tabular-nums">
            항목 {trade.items.length} · 자재 <Money value={trade.material_amount} /> · 노무 <Money value={trade.labor_amount} />
          </span>
          <span className="shrink-0" data-testid="quote-mobile-price-basis">
            {unitPriceHeading(snapshotVatOf(data).mode)}
          </span>
        </div>

        {trade.items.length === 0 && (
          <SectionCard className="text-sm text-muted-foreground">항목이 없습니다.</SectionCard>
        )}
        {trade.items.map((item, i) => (
          <QuoteItemCard key={`${i}-${item.item}`} item={item} testId="quote-mobile-item" />
        ))}

        {trade.condition_text && (
          <p className="rounded-md bg-accent px-2.5 py-2 text-xs text-muted-foreground">* {trade.condition_text}</p>
        )}

        <div className="flex gap-2">
          {prev ? (
            <Button asChild className="min-h-11 min-w-0 flex-1" variant="outline">
              <Link href={tradeHrefIn(base, n - 1)} replace>
                <ChevronLeft />
                <span className="truncate">{prev.name}</span>
              </Link>
            </Button>
          ) : (
            <span className="flex-1" />
          )}
          {next ? (
            <Button asChild className="min-h-11 min-w-0 flex-1" variant="outline">
              <Link href={tradeHrefIn(base, n + 1)} replace>
                <span className="truncate">{next.name}</span>
                <ChevronRight />
              </Link>
            </Button>
          ) : (
            <span className="flex-1" />
          )}
        </div>
      </div>

      <BottomBar className="justify-between">
        <span className="text-sm text-muted-foreground">최종 견적금액</span>
        <Money className="text-xl font-bold" value={data.quote.total} />
      </BottomBar>
    </div>
  );
};
